import type {
  ExternalAssetMentionData,
  MentionEntity,
  ObjectContextMentionData,
} from '@terra/shared-dto-chat-types';
import type { ChatObjectNavigationTarget } from '@terra/ui-design-system';
import type { OpenMainContentTabInput } from '../store/layoutStore';
import { buildExternalNetworkAssetTab } from '../components/tabs/surface-assets/ExternalNetworkAssetsList';
import { resolveFindingOpenFromChat } from './chatObjectNavigationUtils';

type LinkedChatObject = {
  kind: string;
  id: string;
  label?: string;
};

type ChatMainContentTabFallbackTitles = {
  finding: string;
  signal: string;
  endpoint: string;
  webpage: string;
  execution: string;
  skillEditor: string;
  requests: string;
};

export function buildMainContentTabFromObjectContext({
  objectContext,
  label,
}: {
  objectContext: ObjectContextMentionData;
  label?: string;
}): OpenMainContentTabInput | null {
  const title = objectContext.objectLabel || label;

  switch (objectContext.objectKind) {
    case 'Finding':
      return {
        id: `finding-${objectContext.objectId}`,
        title: title || 'Finding Details',
        type: 'finding',
        data: { findingId: objectContext.objectId, mode: 'view' },
      };
    case 'Signal':
      return {
        id: `signal-${objectContext.objectId}`,
        title: title || 'Signal Details',
        type: 'signal',
        data: { signalId: objectContext.objectId },
      };
    case 'EndPoint':
      return {
        id: `endpoint-${objectContext.objectId}`,
        title: title || 'Endpoint',
        type: 'endpoint',
        data: { endpointId: objectContext.objectId },
      };
    case 'WebPage':
      return {
        id: `webpage-${objectContext.objectId}`,
        title: title || 'Web Page',
        type: 'webpage',
        data: { id: objectContext.objectId },
      };
    case 'Trace':
      return {
        id: `executions-${objectContext.objectId}`,
        title:
          title && title !== 'Execution' ? `Execution: ${title}` : 'Execution',
        type: 'executions',
        data: { threadId: objectContext.objectId },
      };
    case 'Skill':
      return null;
    case 'SourceCode':
      return null;
    default:
      return null;
  }
}

export function buildMainContentTabFromMention({
  mention,
  fallbackTitles,
}: {
  mention: MentionEntity;
  fallbackTitles: ChatMainContentTabFallbackTitles;
}): OpenMainContentTabInput | null {
  switch (mention.entityType) {
    case 'ObjectContext': {
      const objectContext = mention.extraData?.objectContext as
        | ObjectContextMentionData
        | undefined;

      if (!objectContext) {
        return null;
      }

      return buildMainContentTabFromObjectContext({
        objectContext,
        label: mention.label,
      });
    }

    case 'Signal':
      return {
        id: `signal-${mention.entityId}`,
        title: mention.label || fallbackTitles.signal,
        type: 'signal',
        data: { signalId: mention.entityId },
      };
    case 'Finding':
      return {
        id: `finding-${mention.entityId}`,
        title: mention.label || fallbackTitles.finding,
        type: 'finding',
        data: { findingId: mention.entityId, mode: 'view' },
      };
    case 'EndPoint':
      return {
        id: `endpoint-${mention.entityId}`,
        title: mention.label || fallbackTitles.endpoint,
        type: 'endpoint',
        data: { endpointId: mention.entityId },
      };
    case 'WebPage':
      return {
        id: `webpage-${mention.entityId}`,
        title: mention.label || fallbackTitles.webpage,
        type: 'webpage',
        data: { id: mention.entityId },
      };
    case 'Trace':
      return {
        id: `executions-${mention.entityId}`,
        title:
          mention.label && mention.label !== 'Execution'
            ? `Execution: ${mention.label}`
            : fallbackTitles.execution,
        type: 'executions',
        data: { threadId: mention.entityId },
      };
    case 'Skill': {
      const skillDraftId =
        typeof mention.extraData?.skillDraftId === 'string'
          ? mention.extraData.skillDraftId
          : undefined;
      const skillId =
        typeof mention.extraData?.skillId === 'string'
          ? mention.extraData.skillId
          : undefined;
      const mode =
        mention.extraData?.mode === 'create' ||
        mention.extraData?.mode === 'edit'
          ? mention.extraData.mode
          : undefined;

      if (skillDraftId && mode) {
        return {
          id: `skill-editor-${skillDraftId}`,
          title: mention.label || fallbackTitles.skillEditor,
          type: 'skill-editor',
          data: { skillDraftId, mode, skillId },
        };
      }

      return {
        id: `skill-${mention.entityId}`,
        title: mention.label,
        type: 'skill',
        data: { skillId: mention.entityId },
      };
    }

    case 'ExternalAsset': {
      const externalAsset = mention.extraData?.externalAsset as
        | ExternalAssetMentionData
        | undefined;

      if (!externalAsset) {
        return null;
      }

      return buildExternalNetworkAssetTab({
        entityKind: externalAsset.assetKind,
        entityId: mention.entityId,
        label: externalAsset.label || mention.label,
      });
    }

    default:
      return null;
  }
}

export function buildMainContentTabFromChatObject({
  target,
  linkedObject,
  fallbackTitles,
}: {
  target: ChatObjectNavigationTarget;
  linkedObject?: LinkedChatObject;
  fallbackTitles: ChatMainContentTabFallbackTitles;
}): OpenMainContentTabInput | null {
  switch (target.type) {
    case 'endpoint':
      return {
        id: `endpoint-${target.id}`,
        title: target.label || target.endpointUrl || fallbackTitles.endpoint,
        type: 'endpoint',
        data: {
          endpointId: target.id,
          endpointUrl: target.endpointUrl,
        },
      };
    case 'finding': {
      const resolved = resolveFindingOpenFromChat({
        targetId: target.id,
        targetLabel: target.label,
        linkedObject,
        preferEditMode: target.mode === 'edit',
      });

      return {
        id: `finding-${resolved.findingId}`,
        title: target.label || fallbackTitles.finding,
        type: 'finding',
        data: { findingId: resolved.findingId, mode: resolved.mode },
      };
    }

    case 'signal':
      return {
        id: `signal-${target.id}`,
        title: target.label || fallbackTitles.signal,
        type: 'signal',
        data: { signalId: target.id },
      };
    case 'webpage':
      return {
        id: `webpage-${target.id}`,
        title: target.label || fallbackTitles.webpage,
        type: 'webpage',
        data: { id: target.id },
      };
    case 'execution':
      return {
        id: `executions-${target.id}`,
        title:
          target.label && target.label !== 'Execution'
            ? `Execution: ${target.label}`
            : fallbackTitles.execution,
        type: 'executions',
        data: { threadId: target.id },
      };
    case 'agent-execution':
      return {
        id: `agent-execution-${target.id}`,
        title: target.label || fallbackTitles.execution,
        type: 'agent-execution',
        data: { executionId: target.id },
      };
    case 'skill-draft':
      return {
        id: `skill-editor-${target.id}`,
        title: target.label || fallbackTitles.skillEditor,
        type: 'skill-editor',
        data: {
          skillDraftId: target.id,
          mode: target.mode,
          skillId: target.skillId,
        },
      };
    case 'request':
      return {
        id: 'requests',
        title: fallbackTitles.requests,
        type: 'requests',
      };
    default:
      return null;
  }
}
